import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { fmtPrice } from "@/context/CartContext";
import { Check } from "lucide-react";

const OrderConfirmation = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch {
        setOrder(null);
      }
    })();
  }, [id]);

  return (
    <div className="lf-container py-20 md:py-28 max-w-3xl" data-testid="order-confirmation-page">
      <div
        className="mx-auto flex items-center justify-center"
        style={{
          width: 64,
          height: 64,
          borderRadius: 9999,
          background: "var(--lf-surface)",
          border: "1px solid var(--lf-gold)",
        }}
      >
        <Check size={28} strokeWidth={1.5} style={{ color: "var(--lf-gold-dark)" }} />
      </div>

      <h1
        className="font-serif text-center mt-8 leading-none"
        style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)",
          fontWeight: 300,
          color: "var(--lf-ink)",
        }}
      >
        Thank you,
        <br />
        <em style={{ color: "var(--lf-gold-dark)" }}>your bouquet is on its way.</em>
      </h1>

      <p
        className="text-center mt-6 text-base md:text-lg leading-relaxed"
        style={{ color: "var(--lf-ink-soft)" }}
      >
        We've received your order and will reach out shortly to confirm timing.
      </p>

      <div
        className="mt-12 p-8 md:p-10"
        style={{ background: "var(--lf-surface)", border: "1px solid var(--lf-border)" }}
      >
        <div className="flex justify-between items-baseline mb-6">
          <span className="lf-eyebrow">Order</span>
          <span className="text-sm font-mono" data-testid="order-id">
            #{id?.slice(0, 8)}
          </span>
        </div>

        {order ? (
          <>
            <ul className="space-y-4">
              {order.items.map((it) => (
                <li key={it.product_id} className="flex justify-between text-sm">
                  <span>
                    {it.name}{" "}
                    <span style={{ color: "var(--lf-ink-soft)" }}>× {it.quantity}</span>
                  </span>
                  <span>{fmtPrice(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <div
              className="mt-6 pt-5 border-t flex justify-between items-baseline"
              style={{ borderColor: "var(--lf-border)" }}
            >
              <span className="lf-eyebrow">Total</span>
              <span
                className="font-serif text-3xl italic"
                style={{ color: "var(--lf-gold-dark)" }}
                data-testid="order-total"
              >
                {fmtPrice(order.total)}
              </span>
            </div>
            <div className="mt-6 text-sm" style={{ color: "var(--lf-ink-soft)" }}>
              <p>
                <span className="lf-eyebrow mr-2">Fulfillment</span>
                {order.fulfillment_type === "DELIVERY"
                  ? `Delivery · ${order.delivery_address || ""}`
                  : "Pickup at the studio · 116 South Main St, Graham NC"}
              </p>
              {order.delivery_date && (
                <p className="mt-2">
                  <span className="lf-eyebrow mr-2">Date</span>
                  {order.delivery_date}
                </p>
              )}
            </div>
          </>
        ) : state ? (
          <p className="text-sm" style={{ color: "var(--lf-ink-soft)" }}>
            Total charged: {fmtPrice(state.total || 0)}
          </p>
        ) : (
          <p className="text-sm" style={{ color: "var(--lf-ink-soft)" }}>
            Loading order details…
          </p>
        )}
      </div>

      <div className="text-center mt-12">
        <Link to="/shop" className="lf-btn-primary" data-testid="confirmation-continue-shopping">
          Continue shopping
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmation;
